'use client';

/**
 * LiveNumber — a value that briefly tick-flashes when it changes, but ONLY when
 * the opt-in Enhanced-UI layer is on. Off (the default), it is a plain span: the
 * number renders byte-identically to a bare `{value}`, no animation, no extra
 * behaviour.
 *
 * How the flash works (CSS does the actual fade — see globals.css):
 *  - The text always sits in a span carrying `sig-livenum` (inert unless
 *    [data-ui="enhanced"]).
 *  - When the value changes *and* Enhanced is on, we add `is-up`/`is-down` for a
 *    single render based on the numeric direction; the keyed remount restarts the
 *    240ms `sig-flash-*` animation even when the same direction repeats.
 *  - First mount never flashes (the previous value seeds to the initial value),
 *    so an SSR/CSR hydrate or a fresh navigation is calm.
 *
 * SSR-safe: the rendered markup on the server and on first client paint is just
 * the value inside `sig-livenum` with no direction class — identical strings —
 * so hydration never mismatches. Direction only ever applies after a real,
 * client-side value change.
 */

import { useEffect, useRef, useState } from 'react';

import { useEnhancedUi } from '@/lib/ui/use-enhanced-ui';
import { cn } from '@/lib/utils';

type Direction = 'up' | 'down' | null;

/** Pull a comparable number out of a value that may be formatted (e.g. "+1.24%"
 * or "$1,240"). Returns null when there's no signed magnitude to compare — then
 * we don't claim a direction. */
function numericOf(value: number | string): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // Keep digits, sign, decimal point; drop currency, %, commas, spaces.
  const cleaned = value.replace(/[^0-9.+-]/g, '');
  if (cleaned === '' || cleaned === '+' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function LiveNumber({
  value,
  className,
  always = false,
}: {
  value: number | string;
  className?: string;
  /**
   * Flash on change regardless of the Enhanced-UI setting — for market data
   * that updates in place (Markets page tapes, map, screener, overview), where
   * the flash is how a reader sees WHICH figure just moved.
   */
  always?: boolean;
}) {
  const enhancedUi = useEnhancedUi();
  const enhanced = enhancedUi || always;

  // Previous numeric value (for direction) and a tick counter that forces a
  // keyed remount of the inner span so the flash animation replays every change.
  const prev = useRef<number | null>(numericOf(value));
  const [tick, setTick] = useState(0);
  const [direction, setDirection] = useState<Direction>(null);

  useEffect(() => {
    const next = numericOf(value);
    const last = prev.current;
    prev.current = next;

    // No flash on first mount (handled by the seeded ref), on a non-numeric
    // value, or when nothing actually moved. Clearing to null on a no-op is a
    // React bail-out (same value → no re-render), so it's cheap.
    if (!enhanced || next === null || last === null || next === last) {
      setDirection(null);
      return;
    }
    setDirection(next > last ? 'up' : 'down');
    setTick((t) => t + 1);
  }, [value, enhanced]);

  return (
    <span
      key={tick}
      className={cn(
        'sig-livenum font-mono tabular-nums',
        always && 'sig-tick',
        enhanced && direction === 'up' && 'is-up',
        enhanced && direction === 'down' && 'is-down',
        className,
      )}
    >
      {value}
    </span>
  );
}
